import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';
import { DataserviceService } from '../dataservice.service';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.css'],
})
export class SignInComponent {
  error: string | null = null;
  successMessage: string | null = null;
  otpForm: FormGroup;
  otpSent: boolean = false;
  loginForm: FormGroup;
  loginTab: any;
  isSendingOtp = false;
  isVerifyingOtp = false;
  loadingMessage = '';
  systemMessages: any[] = [];

  constructor(@Inject(FormBuilder) private fb: FormBuilder, private supabaseService: DataserviceService, @Inject(Router) private router: Router) {


    this.loginForm = new FormGroup({
      email: new FormControl(null),
      phone: new FormControl(null),
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });

  }

  ngOnInit() {
    this.loginTab = 'Phone';
    this.loginForm.get('email')?.setValidators(null);
    this.loginForm.get('phone')?.setValidators([Validators.required, Validators.pattern(/^(1\d{10}|\d{10})$/)])

    this.loginForm.updateValueAndValidity();
    this.getSystemMessages();
  }

  async getSystemMessages() {
    try {
      const res = await firstValueFrom(this.supabaseService.getData('announcements/systemMessages'));
      const messages = Array.isArray(res) ? res : [];
      this.systemMessages = messages.filter((msg) => !!msg?.message);
    } catch (error) {
      console.error('Failed to load system messages', error);
      this.systemMessages = [];
    }
  }

  tabChanged(ev: any) {
    if (ev.tab?.nzTitle == 'Email') {
      this.loginTab = 'Email'
      this.loginForm.get('email')?.setValidators([Validators.required, Validators.email]),
        this.loginForm.get('phone')?.setValidators(null);
    } else {
      this.loginTab = 'Phone'
      this.loginForm.get('email')?.setValidators(null);
      this.loginForm.get('phone')?.setValidators([Validators.required, Validators.pattern(/^(1\d{10}|\d{10})$/)])
    }

    this.loginForm.updateValueAndValidity();
  }

  async sendOtp() {
    this.error = null;
    this.successMessage = null;
    this.loadingMessage = 'Sending OTP...';
    this.isSendingOtp = true;
    if (this.loginTab == 'Phone' && this.loginForm.get('phone')?.value) {
      this.phoneOtpFlow();
    } else {
      this.emailOtpFlow();
    }

  }

  phoneOtpFlow() {
      try {
        this.isSendingOtp = false;
        this.clearLoadingMessageIfIdle();
        this.supabaseService.signInWithPhone('1' + this.loginForm.get('phone')?.value)
        .subscribe((r:any) => {
          if(r.result == 'success') {
            this.otpSent = true;
            this.successMessage = 'OTP sent. Please check your phone.';
          } else {
            this.error = r.message || 'Error Sending Pin';
          }
        }, e => {
          this.error = e.message || 'Phone Not Found';
        })
      } catch(e:any) {
        this.error = e.message || 'Error Connecting To Server';
      }
  }

  async phoneVerifyOtpFlow() {
    const { phone } = this.loginForm.value;
    const { otp } = this.otpForm.value;

    this.supabaseService.verifyPhoneOtp({ phone: '1' + phone, otp: otp })
    .subscribe(async r => {
      if(r.result == 'success') {
        const session = r.data?.session;
        localStorage.setItem('access_token', session.access_token);
        try {
          localStorage.removeItem('user')
          const user = await this.supabaseService.getUser().toPromise();
          if (!user) throw new Error('User data is empty');

          localStorage.setItem('user', JSON.stringify(user));

          await this.handlePostLoginNavigation(user);
        } catch (err) {
          this.error = err instanceof Error ? err.message : 'Unexpected error verifying user.';
        }
      } else {
        this.error = r.message || 'Pin expired or invalid';
      }
    }, e => {
      this.error = e.message || 'Error Verifying Pin';
    })
  }

  emailOtpFlow() {
    try {
      this.isSendingOtp = false;
      this.clearLoadingMessageIfIdle();
      this.supabaseService.signInWithEmail(this.loginForm.get('email')?.value)
      .subscribe((r:any) => {
        if(r.result == 'success') {
          this.otpSent = true;
          this.successMessage = 'OTP sent. Please check your email.';
        } else {
          this.error = r.message || 'Error Sending Pin';
        }
      }, e => {
        this.error = e.message || 'Email Not Found';
      })
    } catch(e:any) {
      this.error = e.message || 'Error Connecting To Server';
    }
  }

  async emailVerifyOtpFlow() {
    const { email } = this.loginForm.value;
    const { otp } = this.otpForm.value;

    this.supabaseService.verifyEmailOtp({ email: email, otp: otp })
    .subscribe(async r => {
      if(r.result == 'success') {
        const session = r.data?.session;
        localStorage.setItem('access_token', session.access_token);
        try {
          localStorage.removeItem('user')
          const user = await this.supabaseService.getUser().toPromise();
          if (!user) throw new Error('User data is empty');

          localStorage.setItem('user', JSON.stringify(user));

          await this.handlePostLoginNavigation(user);
        } catch (err) {
          this.error = err instanceof Error ? err.message : 'Unexpected error verifying user.';
        }
      } else {
        this.error = r.message || 'Pin expired or invalid';
      }
    }, e => {
      this.error = e.message || 'Error Verifying Pin';
    })
  }

  async verifyOtp() {
    if (this.isVerifyingOtp) {
      return;
    }

    this.error = null;
    this.successMessage = null;
    localStorage.removeItem('isImpersonating');
    this.isVerifyingOtp = true;
    this.loadingMessage = 'Verifying OTP...';

    try {
      if (this.loginTab == 'Phone') {
        await this.phoneVerifyOtpFlow()
      } else {
        await this.emailVerifyOtpFlow();
      }
    } finally {
      this.isVerifyingOtp = false;
      this.clearLoadingMessageIfIdle();
    }

  }


  stripFirstDigit(event: any) {
    const input = event.target.value;
    if (input.length > 10) {
      if (input[0] === '1') {
        event.target.value = input.slice(1);
      } else {
        event.target.value = input.slice(0, 10);
      }
    }
  }

  get isLoading(): boolean {
    return this.isSendingOtp || this.isVerifyingOtp;
  }

  private clearLoadingMessageIfIdle() {
    if (!this.isLoading) {
      this.loadingMessage = '';
    }
  }

  private async handlePostLoginNavigation(user: any) {
    this.successMessage = "You're in! Taking you to your portals...";
    setTimeout(() => this.router.navigate(['/portals']), 2000);
  }

  private async checkPendingQuizzes(userId: string): Promise<boolean> {
    if (!userId) return false;
    try {
      const res: any = await firstValueFrom(
        this.supabaseService.getData('quizzes/needed-quizzes', { params: { user_id: userId } }).pipe(
          catchError(() => of({ data: [] }))
        )
      );
      const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      return rows.length > 0;
    } catch {
      return false;
    }
  }

  trackByMessage(index: number, item: any) {
    return item?.id ?? item?.message ?? index;
  }

}
